import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ArrowLeft, Video, Lock, Play } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ToolSelector from '../components/ToolSelector';
import PageTutorial from '../components/PageTutorial';
import PageHero from '../components/PageHero';
import AdDisplay from '../components/AdDisplay';

const VideoLibrary = () => {
 const { API, user } = useContext(AuthContext);
 const navigate = useNavigate();
 const [videos, setVideos] = useState([]);
 const [filteredVideos, setFilteredVideos] = useState([]);
 const [categoryFilter, setCategoryFilter] = useState('all');
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 fetchVideos();
 }, []);

 useEffect(() => {
 filterVideos();
 }, [categoryFilter, videos]);

 const fetchVideos = async () => {
 try {
 const response = await axios.get(`${API}/videos`);
 setVideos(response.data);
 } catch (error) {
 toast.error('Failed to load videos');
 } finally {
 setLoading(false);
 }
 };

 const filterVideos = () => {
 if (categoryFilter === 'all') {
 setFilteredVideos(videos);
 } else {
 setFilteredVideos(videos.filter(v => v.category.toLowerCase() === categoryFilter.toLowerCase()));
 }
 };

 const categories = ['All', 'Beginner', 'Advanced', 'Business', 'Technical'];

 return (
 <div className="min-h-screen bg-background">
 <PageHero
 eyebrow="Video library"
 eyebrowIcon={Video}
 title={<>Training <span className="text-amber-brand">videos</span></>}
 description="Expert freeze-drying tutorials — beginner through advanced."
 backTo="/dashboard"
 backLabel="Dashboard"
 testId="video-library-hero"
 />

 <ToolSelector />

 <div className="container mx-auto px-4 pt-4">
   <AdDisplay location="videos_header" className="flex justify-center mb-4" />
 </div>

 <div className="container mx-auto px-4 py-8">
 <PageTutorial id="videos" />
 <Card className="mb-6 shadow-lg">
 <CardHeader>
 <CardTitle>Training & Tutorial Videos</CardTitle>
 <CardDescription>Learn from expert freeze-drying tutorials</CardDescription>
 </CardHeader>
 <CardContent>
 <Select value={categoryFilter} onValueChange={setCategoryFilter}>
 <SelectTrigger className="w-full md:w-48" data-testid="category-filter">
 <SelectValue placeholder="Category" />
 </SelectTrigger>
 <SelectContent>
 {categories.map(cat => (
 <SelectItem key={cat} value={cat.toLowerCase()}>{cat}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 </CardContent>
 </Card>

 {loading ? (
 <div className="text-center py-12">
 <p className="text-slate-500">Loading videos...</p>
 </div>
 ) : filteredVideos.length === 0 ? (
 <Card className="text-center py-12">
 <CardContent>
 <Video className="w-16 h-16 text-slate-300 mx-auto mb-4" />
 <p className="text-slate-500">No videos available yet</p>
 </CardContent>
 </Card>
 ) : (
 <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
 {filteredVideos.map(video => (
 <Card key={video.id} className="shadow-lg overflow-hidden card-hover" data-testid="video-card">
 <div className="relative aspect-video bg-slate-200 flex items-center justify-center">
 {video.is_pro_only && user?.role !== 'pro' && user?.role !== 'admin' ? (
 <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
 <div className="text-center text-white">
 <Lock className="w-12 h-12 mx-auto mb-2" />
 <p className="font-semibold">Pro Only</p>
 </div>
 </div>
 ) : (
 <Play className="w-16 h-16 text-white" />
 )}
 </div>
 <CardHeader>
 <div className="flex items-start justify-between gap-2">
 <CardTitle className="text-lg">{video.title}</CardTitle>
 {video.is_pro_only && (
 <span className="inline-flex items-center gap-1 bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-1 rounded">
 <Lock className="w-3 h-3" />
 Pro
 </span>
 )}
 </div>
 <CardDescription>{video.description}</CardDescription>
 </CardHeader>
 <CardContent>
 <span className="inline-block bg-orange-100 text-orange-800 text-xs font-medium px-2.5 py-0.5 rounded">
 {video.category}
 </span>
 {(!video.is_pro_only || user?.role === 'pro' || user?.role === 'admin') && video.url && (
 <Button
 className="w-full mt-4"
 onClick={() => window.open(video.url, '_blank')}
 data-testid="watch-video-btn"
 >
 Watch Video
 </Button>
 )}
 </CardContent>
 </Card>
 ))}
 </div>
 )}
 </div>
 </div>
 );
};

export default VideoLibrary;
